/*
  Charmlry Customizer Chat — conversational experience.
  Ported from the Claude Design prototype "Necklace Atelier" and built on top
  of the v2 engine: <configurator-chat-element> EXTENDS ConfiguratorModal
  (assets/configurator-v2.js), so product config, per-product rules, image
  cascades, name validation, pricing, cart plumbing (buildItems/cartAdd,
  order property schema), bundle offers, session persistence and cart-edit
  are the very same code. This file only adds the chat state machine, the
  chat rendering and its actions. The classic modal is untouched.
*/
const G = window.CharmlryConfigurator;
const { ConfiguratorModal, FINISHES, STYLES, DESIGNERS, esc, chatWanted } = G;

const TYPING_LEAD = 380;

class ConfiguratorChat extends ConfiguratorModal {
  connectedCallback() {
    if (this._init) return;
    const wrap = this.closest('[data-customizer-chat]');
    const d = wrap ? wrap.dataset : {};
    this.opts = {
      pace: Math.max(0, +d.pace || 650),
      intro: d.intro || "Hi, I'm {designer}, the designer who'll draw your monogram by hand.",
      intro2: d.intro2 || '✨ A few quick choices to customize your piece.\n✍️ **Every monogram is hand-drawn after you order**, so what you see here is a sample.\n📩 **Your unique design arrives within 24 to 72 hours.**\nReady?',
      outro: d.outro || '',
    };
    this._timers = [];
    super.connectedCallback();
    if (!this.cfg) return;
    this.KEY = 'charmlry.cfgchat.v1.' + this.cfg.product.handle;
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      const t = e.target;
      if (!t || !t.dataset || !t.dataset.input) return;
      e.preventDefault();
      if (t.dataset.input === 'cname' || t.dataset.input === 'mf-cname') {
        // Enter moves to the next charm, the last one sends
        const nx = this.root.querySelector(`[data-input="${t.dataset.input}"][data-k="${(+t.dataset.k || 0) + 1}"]`);
        if (nx) { nx.focus(); return; }
        if (t.dataset.input === 'cname') { if (this.state.cEdit !== 'name') this.sendName(); } else if (!this.state.bEdit) this.sendBName();
        return;
      }
      if (t.dataset.input === 'name') this.sendName();
      else if (t.dataset.input === 'mf-name') this.sendBName();
      else if (t.dataset.input === 'ce-name') this.saveCeName();
      else if (t.dataset.input === 'pce-name') { const b = this.root.querySelector('[data-action="c-pce-name-save"]'); if (b) b.click(); }
    });
    // Cart "Edit design" deep link, when this page uses the chat experience.
    const editKey = new URLSearchParams(window.location.search).get('cfg_edit');
    if (editKey && chatWanted()) setTimeout(() => this.openCartEdit(editKey), 0);
  }

  /* ---------- state ---------- */
  fresh() {
    return Object.assign(super.fresh(), {
      cStarted: false, cStep: 0, cTyping: false, cReopen: null, cExtrasDone: false, cGiftsDone: false,
      cAdded: false, cDirty: false, cEdit: null, ceName: '', cPending: false,
      bStep: -1, bEdit: null, bceName: '', bAdded: 0, bPending: false, bReopen: null, pEdit: null, pceName: '', bFollowPending: false,
    });
  }
  chatSteps() {
    // New flow (?cfg_new=1): one intro line, then straight to the first choice.
    const s = this.newFlow ? ['intro'] : ['intro', 'intro2'];
    if (this.cfg.hasCord) s.push('cord');
    s.push('material');
    if (this.cfg.multiCharm) s.push('charms');
    if (this.cfg.hasLength) s.push('length');
    s.push('name', 'style');
    if (!this.cfg.hideStones) s.push('stones');
    s.push('gifts', 'summary', 'deal');
    return s;
  }
  stepIdx(id) { return this.chatSteps().indexOf(id); }
  curStepId() { return this.chatSteps()[this.state.cStep]; }
  designer() { return DESIGNERS[(this.state.designerIdx || 0) % DESIGNERS.length]; }
  /* Editor text → safe HTML: **bold**, line breaks, {designer} */
  rich(t) {
    return esc(t || '')
      .replace(/\*\*(.+?)\*\*/g, '<b>$1</b>')
      .replace(/\r?\n/g, '<br>')
      .replace(/\{designer\}/g, '<b>' + esc(this.designer().name) + '</b>');
  }
  designerImg() { return ((this.cfg.images || {}).designers || {})[this.designer().name.toLowerCase()] || null; }
  pace() { return this.opts.pace; }
  /* Typing time for a message: the base pace, plus extra for long texts so
     the shopper has time to read the previous bubble (capped). */
  typingMs(id) {
    const base = this.pace();
    if (id === 'intro2') { const len = (this.opts.intro2 || '').length; return base + Math.min(1800, Math.max(0, (len - 60) * 8)); }
    return base;
  }
  later(fn, ms) { this._timers.push(setTimeout(fn, ms)); }
  clearTimers() { (this._timers || []).forEach(clearTimeout); this._timers = []; }

  save() {
    if (this.state && this.state.cartEdit) return;
    try {
      const s = Object.assign({}, this.state, { toast: null, helpSheet: null, exitSheet: false, zoomStyle: null, extraSheet: null, confetti: false, busy: false, inlineEdit: null, cTyping: false, cPending: false, bPending: false, bFollowPending: false });
      sessionStorage.setItem(this.KEY, JSON.stringify(s));
    } catch (err) { /* storage unavailable */ }
  }

  /* ---------- lifecycle ---------- */
  open(e) {
    // The theme's declarative trigger belongs to the classic modal.
    if (e && e.target && e.target.closest && e.target.closest('[on\\:click="configurator-modal-element/open"]')) return;
    const wasOpen = this.hasAttribute('open');
    if (!wasOpen) { this._sigs = null; this._firstNew = null; this._firstNewSig = null; this._turnAnchorSig = null; }
    this._turnOpen = true;
    super.open(e);
    if (wasOpen || !this.state) return;
    if (!this.state.cStarted) {
      this.state.cStarted = true;
      this.save();
      this.chain([1]); // intro -> "Ready?" (the tap on "Yes, I'm ready" continues); new flow: intro -> first choice
    } else {
      this.later(() => { this._firstNew = null; this._firstNewSig = null; this.scrollFeed(true); }, 60);
    }
  }
  close() { this.clearTimers(); if (this._stopHold) this._stopHold(); this.state.cTyping = false; super.close(); }

  /* The chat reports its own conversation steps (chat:<id>). The screen changes
     it inherits from the v2 engine describe the same moment, so reporting both
     would count every step twice in the A/B funnel. */
  emitStep(step) {
    const s = String(step);
    if (s.indexOf('chat:') !== 0) return;
    super.emitStep(s);
  }

  /* The first piece left the cart (see syncPiecesWithCart): the chat goes back
     to the summary, ready to add the design again. */
  piecesGone() {
    super.piecesGone();
    Object.assign(this.state, { cAdded: false, cDirty: false, cEdit: null, bStep: -1, bAdded: 0, bEdit: null, bReopen: null, pEdit: null, cStep: Math.min(this.state.cStep, this.stepIdx('summary')) });
    this.save();
    this.render(true);
  }

  async openCartEdit(key) {
    await super.openCartEdit(key);
    this.clearTimers();
    if (!this.state.cartEdit) return;
    Object.assign(this.state, { cStarted: true, cStep: this.stepIdx('summary'), cTyping: false, cReopen: null, cExtrasDone: true, cGiftsDone: true, cAdded: false, cDirty: true, cEdit: null });
    this.render(true);
  }

  /* ---------- conversation flow ---------- */
  setStep(n) {
    if (n <= this.state.cStep) return;
    this.state.cTyping = false;
    this.state.cPending = false;
    this.state.cStep = n;
    this.save();
    this.render(true);
    this.emitStep('chat:' + (this.chatSteps()[n] || n));
  }
  advance(n) {
    if (n <= this.state.cStep) { this.render(); return; }
    this.later(() => { this.state.cTyping = true; this.render(true); }, TYPING_LEAD);
    this.later(() => this.setStep(n), TYPING_LEAD + this.pace());
  }
  chain(ns) {
    let delay = 350;
    ns.forEach((n) => {
      this.later(() => { this.state.cTyping = true; this.render(true); }, delay);
      delay += this.typingMs(this.chatSteps()[n]);
      this.later(() => this.setStep(n), delay);
      delay += 420;
    });
  }
  advanceNext() { this.advance(this.state.cStep + 1); }
  /* Confirm the current answer: while re-opening an earlier answer it just
     closes; otherwise the designer moves to the next question. */
  answer(patch) {
    const S = this.state;
    if (patch) Object.assign(S, patch);
    this.collapseThen(() => {
      if (S.cReopen != null) {
        S.cReopen = null;
        if (S.cAdded) S.cDirty = true;
        this.save(); this.render();
        return;
      }
      // The answer bubble shows right away; the next question follows the typing.
      S.cPending = true;
      this.save(); this.render(true);
      this.advanceNext();
    });
  }
  /* Fold the open widget (or quick reply) before the state moves on, so the
     interaction turns into the answer bubble instead of vanishing. */
  collapseThen(fn) {
    const feed = this.feedEl();
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const els = feed ? [...feed.querySelectorAll('.cht__widget:not(.cht__widget--dim), .cht__row--reply')] : [];
    const w = els[els.length - 1];
    if (!w || reduce) { fn(); return; }
    w.style.height = w.offsetHeight + 'px';
    w.style.overflow = 'hidden';
    w.getBoundingClientRect();
    w.classList.add('cht__collapsing');
    w.style.height = '0px';
    this.later(fn, 250);
  }
  /* A kind of text typed instead of the text itself ("Family initials",
     "Initials"): the designer can't draw it, so the chat asks for the
     letters. Chat orders in Sep–Oct 2026 arrived like this. Returns 'type' or
     null. "Family" alone counts only on family products, where the monogram
     is always initials. "Same" is accepted as typed (Paolo, 2026-10-19). */
  typeWord(t) {
    const x = String(t || '').trim().toLowerCase().replace(/[.!?,\s]+$/, '').replace(/\s+/g, ' ');
    if (!x) return null;
    if (/^((our|my|the) )?(family|family's|families)( (initials?|initial letters|names?|name initials|logo|monogram))?( but .*)?$/.test(x) && (x !== 'family' || this.famCtx())) return 'type';
    if (/^((our|my|the|family) )?initials?( of (the|my|our) family)?$/.test(x)) return 'type';
    if (/^(a |one |two |full |first |last )?(names?|surname|word|short word|monogram|text)$/.test(x)) return 'type';
    return null;
  }
  famCtx() {
    const h = this.mfMode() ? (this.state.mfOffer || '') : (this.cfg.product.handle || '') + ' ' + (this.cfg.product.title || '');
    return /family/i.test(h);
  }
  charmNameOk(t) { return super.charmNameOk(t) && !this.typeWord(t); }
  nameValid() { return super.nameValid() && (this.cfg.multiCharm || !this.typeWord(this.state.nameText)); }
  mfNameValid() { return super.mfNameValid() && (this.ctxMulti() || !this.typeWord((this.state.mf || {}).nameText)); }
  nameMsgsHtml() {
    const base = super.nameMsgsHtml();
    const mf = this.mfMode();
    const texts = this.ctxMulti() ? ((mf ? this.state.mf : this.state).names || []) : [mf ? (this.state.mf.nameText || '') : this.state.nameText];
    if (!texts.some((x) => this.typeWord(x))) return base;
    const msg = this.famCtx() ? 'Type the first letter of each name, for example B, L, P, T. I weave them into one family monogram.' : 'Type the text itself: initials like B, L, P, T, a name like David, or a word like Love.';
    return `<div class="cfg__msg cfg__msg--warn"><span class="cfg__msg-icon">⚠︎</span><span>${msg}</span></div>`;
  }
  namePlaceholder() { return this.famCtx() ? 'e.g. B, L, P, T' : 'e.g. Nick, BLPT, Love…'; }

  sendName() {
    this._turnOpen = true;
    if (this.mfMode()) { this.sendBName(); return; }
    if (!this.nameValid()) { this.render(); return; }
    if (this.cfg.multiCharm) {
      const S = this.state;
      S.names = (S.names || []).map((x) => String(x || '').trim());
      this.syncNames(S);
      this.answer();
      return;
    }
    this.answer({ nameText: this.state.nameText.trim() });
  }

  /* bundle sub-chat: steps follow the engine's mfSteps(), details split in
     stones / gifts / summary like the prototype */
  bSteps() {
    const s = [];
    this.mfSteps().forEach((id) => {
      if (id === 'mf-details') {
        const o = this.offerOf(this.state.mfOffer) || {};
        if (!o.hideStones) s.push('mf-stones');
        s.push('mf-gifts', 'mf-summary');
      } else s.push(id);
    });
    return s;
  }
  bScreenFor(id) { return (id === 'mf-stones' || id === 'mf-gifts' || id === 'mf-summary') ? 'mf-details' : id; }
  setBStep(n) {
    const steps = this.bSteps();
    if (n >= steps.length) n = steps.length - 1;
    this.state.cTyping = false;
    this.state.bPending = false;
    this.state.bStep = n;
    this.state.screen = this.bScreenFor(steps[n]);
    this.emitStep('chat:' + steps[n]);
    this.save();
    this.render(true);
  }
  advanceB(n) {
    this.later(() => { this.state.cTyping = true; this.render(true); }, 300);
    this.later(() => this.setBStep(n), 300 + this.pace());
  }
  advanceBNext() {
    const S = this.state;
    if (S.bReopen != null) {
      this.collapseThen(() => {
        // keep the current step by id: the step list can change (a saved
        // design drops the style question)
        const curId = this.bSteps()[S.bStep];
        S.bReopen = null;
        const ni = this.bSteps().indexOf(curId);
        S.bStep = ni !== -1 ? ni : Math.min(S.bStep, this.bSteps().length - 1);
        S.screen = this.bScreenFor(this.bSteps()[S.bStep]);
        this.save(); this.render();
      });
      return;
    }
    this.collapseThen(() => {
      S.bPending = true;
      this.save(); this.render(true);
      this.advanceB(S.bStep + 1);
    });
  }
  startMf(offer) {
    super.startMf(offer);
    this.state.bStep = -1; this.state.bEdit = null; this.state.bceName = ''; this.state.bReopen = null;
    this.save(); this.render(true);
    this.advanceB(0);
  }
  cancelMf() { this.clearTimers(); this.st({ screen: 'deal', mfOffer: null, mf: null, editingPieceIdx: null, bStep: -1, bEdit: null, bReopen: null, cTyping: false }); }
  sendBName() {
    if (!this.mfNameValid()) { this.render(); return; }
    if (this.ctxMulti()) { const m = this.state.mf; m.names = (m.names || []).map((x) => String(x || '').trim()); this.syncNames(m); }
    Object.assign(this.state.mf, { nameText: (this.state.mf.nameText || '').trim() });
    this.advanceBNext();
  }
  async mfAdd() {
    const before = this.state.pieces.length;
    await super.mfAdd();
    if (!this.mfMode() && (this.state.pieces.length > before || this.state.editingPieceIdx == null)) {
      this.state.bStep = -1; this.state.bAdded = (this.state.bAdded || 0) + 1;
      // The recap card shows at once; the designer's follow-up (and the
      // offers again) come after a longer pause, so the recap gets read.
      this.state.bFollowPending = true;
      this.save(); this.render(true);
      this.clearTimers();
      this.later(() => { this.state.cTyping = true; this.render(true); }, 900);
      this.later(() => { this.state.cTyping = false; this.state.bFollowPending = false; this.save(); this.render(true); }, 900 + this.pace() + 700);
    }
  }

  /* Edit a piece that is already in the set: change locally, then "Update
     my piece" replaces its cart lines (same plumbing as the first piece). */
  pSet(idx, patch, keepOpen) {
    const S = this.state;
    const pieces = S.pieces.slice();
    const p = pieces[idx]; if (!p) return;
    pieces[idx] = Object.assign({}, p, { cfg: Object.assign({}, p.cfg, patch), dirty: true });
    this.st({ pieces, pEdit: keepOpen ? S.pEdit : null });
  }
  async updatePiece(idx) {
    const S = this.state;
    const p = S.pieces[idx];
    const target = p && this.pieceTarget(p);
    if (!target || S.busy) return;
    if (target.multiCharm && !this.multiValid(p.cfg)) { this.showToast('Add a name for each charm.'); return; }
    const cfg = Object.assign({}, p.cfg); cfg.createdAt = Date.now();
    if (target.multiCharm) this.syncNames(cfg);
    this.st({ busy: true });
    try {
      await this.cartRemoveKeys(p.lineKeys);
      const keys = await this.cartAdd(this.buildItems(cfg, target));
      const pieces = S.pieces.slice();
      pieces[idx] = Object.assign({}, p, { cfg, lineKeys: keys, dirty: false });
      this.st({ pieces, busy: false, pEdit: null }, () => this.showToast('Updated. Your cart reflects the changes.'));
    } catch (err) {
      this.st({ busy: false });
      this.showToast(err.message || 'Something went wrong. Please try again.');
    }
  }
  pieceSummaryCard(idx) {
    const S = this.state;
    const p = S.pieces[idx]; if (!p) return '';
    const o = this.offerOf(p.offerHandle) || {};
    const m = p.cfg;
    const k = S.pEdit && S.pEdit.idx === idx ? S.pEdit.key : null;
    const row = (key, label, value, editor) => `<div class="cht__sum-row" data-action="c-p-edit" data-p="${idx}" data-k="${key}"><span class="cht__sum-label">${label}</span><span class="cht__sum-val">${value} <span class="cht__sum-pen">✎</span></span></div>${k === key ? `<div class="cht__sum-editor">${editor}</div>` : ''}`;
    const cords = o.cords && o.cords.length ? o.cords : this.cordsFor();
    const lengths = o.lengths && o.lengths.length ? o.lengths : [16, 18, 20, 22];
    const rows = [];
    if (o.hasCord) rows.push(row('cord', 'Cord', esc(this.cordOf(m.cord, cords).name), cords.map((c) => `<button ${this.chipB(m.cord === c.id)} data-action="c-pce-cord" data-p="${idx}" data-id="${c.id}"><span class="cht__chip-dot" style="background:${c.c}"></span>${c.name}</button>`).join('')));
    const pt = this.pieceTarget(p) || {};
    rows.push(row('finish', 'Finish', esc(this.finishOf(m.finish).name), this.finishList(pt.finishIds).map((f) => `<button ${this.chipB(m.finish === f.id)} data-action="c-pce-finish" data-p="${idx}" data-id="${f.id}"><span class="cht__chip-dot" style="background:${f.grad}"></span>${f.name}</button>`).join('')));
    if (o.hasLength) rows.push(row('length', 'Chain length', this.lengthLabel(m.length || this.defaultLength(lengths)), lengths.map((L) => `<button ${this.chipB(m.length === L)} data-action="c-pce-length" data-p="${idx}" data-n="${L}">${L} in · ${Math.round(L * 2.5)} cm</button>`).join('')));
    if (pt.multiCharm) {
      rows.push(row('charms', 'Charms', esc(this.charmsLabel(m)), this.charmChips(m, 'c-pce-charms', idx, pt.maxCharms)));
      rows.push(row('name', 'Names', '“' + esc(this.namesDisplayOf(m.nameText)) + '”', `<div class="cht__charm-fields">${this.charmFieldsHtml(m, 'pce-cname')}</div><button class="cht__send" data-action="c-p-edit" data-p="${idx}" data-k="name">Done</button>`));
    } else rows.push(row('name', 'Monogram', '“' + esc(this.namesDisplayOf(m.nameText)) + '”', `<div class="cht__input-row"><input class="cfg__input" data-input="pce-name" value="${esc(k === 'name' ? (S.pceName || '') : (m.nameText || ''))}" maxlength="40" autocomplete="off"><button class="cht__send" data-action="c-pce-name-save" data-p="${idx}">Save</button></div>`));
    rows.push(row('style', 'Monogram style', esc((this.styleOf(m.style) || {}).name || '…'), STYLES.map((st) => `<button ${this.chipB(m.style === st.id)} data-action="c-pce-style" data-p="${idx}" data-id="${st.id}">${this.styleThumbHtml(st, m.finish)}${st.name}</button>`).join('')));
    if (!o.hideStones) {
      const n = this.stoneCount(m);
      rows.push(row('stones', 'Birthstones', n ? esc(n + (n > 1 ? ' stones' : ' stone') + ' · +' + this.fmt(n * (o.stonePrice || this.cfg.stonePrice))) : 'None', `<div class="cfg__stones-row" style="padding:2px;margin:0 -2px">${this.stoneCardsHtml(m, { mini: true, stonePrice: o.stonePrice, stoneFiles: o.stoneFiles, stoneImages: o.stoneImages, stones: o.stones || null, prefix: 'pstone' })}</div>`));
    }
    const gN = (m.scratch ? 1 : 0) + (m.gift ? 1 : 0);
    rows.push(row('gifts', 'Gift extras', gN ? esc(gN + (gN > 1 ? ' extras' : ' extra') + ' · +' + this.fmt((m.scratch ? this.cfg.scratch.price : 0) + (m.gift ? this.cfg.gift.price : 0))) : 'None', `<button ${this.chipB(!!m.scratch)} data-action="c-pce-scratch" data-p="${idx}">${m.scratch ? '✓' : '+'} Scratch card · ${this.fmt(this.cfg.scratch.price)}</button><button ${this.chipB(!!m.gift)} data-action="c-pce-gift" data-p="${idx}">${m.gift ? '✓' : '+'} Gift box · ${this.fmt(this.cfg.gift.price)}</button>`));
    const base = o.dealPrice != null ? o.dealPrice : p.base;
    const total = base + this.addonPrice(m, { stonePrice: o.stonePrice });
    let cta;
    if (S.busy) cta = `<button class="cfg__btn-solid cht__continue" disabled>Updating…</button>`;
    else if (p.dirty) cta = `<button class="cfg__btn-solid cht__continue" data-action="c-p-update" data-p="${idx}">Update my piece · ${this.fmt(total)}</button>`;
    else cta = `<div class="cfg__ok-note"><span class="cfg__ok-check">✓</span><span>In your cart · ${this.fmt(total)}</span></div>`;
    const removeLink = S.busy ? '' : `<button class="cht__link" data-action="c-remove-piece" data-i="${idx}">Remove from my set</button>`;
    return `<div class="cht__sum" data-c-piece="${idx}">
      <div class="cht__sum-head"><span class="cht__sum-title cfg-serif">${esc(o.title || p.kind)} “${esc(this.namesDisplayOf(m.nameText))}”</span><span class="cht__sum-price">${this.fmt(base + this.charmExtra(m))}</span></div>
      <span class="cht__sum-kicker">TAP ANY DETAIL TO CHANGE IT</span>
      <div class="cht__sum-rows">${rows.join('')}</div>
      ${cta}
      ${removeLink}
    </div>`;
  }

  /* Remove a bundle piece from the set (and its cart lines). */
  async removePiece(i) {
    const S = this.state;
    const p = S.pieces[i];
    if (!p || !p.offerHandle || S.busy) return;
    this.st({ busy: true });
    try {
      await this.cartRemoveKeys(p.lineKeys);
      const pieces = S.pieces.filter((x, j) => j !== i);
      const extras = pieces.filter((x) => x.offerHandle).length;
      this.st({ pieces, busy: false, inlineEdit: null, pEdit: null, bAdded: extras ? S.bAdded : 0 }, () => this.showToast('Removed from your set.'));
    } catch (err) {
      this.st({ busy: false });
      this.showToast('Could not remove it. Please try again.');
    }
  }

  /* main piece → cart (first time adds, later edits replace the cart line) */
  async continueSummary() {
    const S = this.state;
    if (S.busy || !this.nameValid() || !S.style) { this.render(); return; }
    if (S.cartEdit) { await super.addToCart(); return; }
    if (S.cAdded && S.pieces.length) {
      if (!S.cDirty) { this.advance(this.stepIdx('deal')); return; }
      S.editingPieceIdx = 0;
    }
    const before = S.pieces.length;
    const wasAdded = S.cAdded;
    await super.addToCart();
    const ok = this.state.pieces.length > before || (wasAdded && this.state.editingPieceIdx == null && !this.state.busy);
    if (!ok) return;
    Object.assign(this.state, { cAdded: true, cDirty: false, cEdit: null, screen: 'deal' });
    this.save(); this.render(true);
    if (this.state.cStep < this.stepIdx('deal')) this.advance(this.stepIdx('deal'));
  }

  /* ---------- events ---------- */
  onClick(e) {
    const el = e.target.closest('[data-action]');
    if (!el || !this.contains(el)) return;
    const a = el.dataset.action;
    if (a !== 'close' && a !== 'backdrop') this._turnOpen = true; // the next new content starts a turn
    const S = this.state;
    const i = el.dataset.i != null ? +el.dataset.i : null;
    const mine = {
      'c-continue': () => this.answer(),
      'c-ready': () => this.answer(),
      'zoom-pick': () => { this.setCur({ style: S.zoomStyle }); this.st({ zoomStyle: null }); if (this.mfMode()) { if (this.bSteps()[S.bStep] === 'mf-style') this.advanceBNext(); } else if (this.curStepId() === 'style' || S.cReopen === this.stepIdx('style')) this.answer(); },
      'c-style': () => { this.setCur({ style: el.dataset.id }); if (this.mfMode()) this.advanceBNext(); else this.answer(); },
      'c-send-name': () => this.sendName(),
      'c-done-stones': () => { if (this.mfMode()) { this.advanceBNext(); return; } this.answer({ cExtrasDone: true }); },
      'c-done-gifts': () => { if (this.mfMode()) { this.advanceBNext(); return; } this.answer({ cGiftsDone: true }); },
      'c-edit': () => {
        const idx = +el.dataset.step;
        const id = this.chatSteps()[idx];
        const patch = { cReopen: idx, cEdit: null };
        if (id === 'stones') patch.cExtrasDone = false;
        if (id === 'gifts') patch.cGiftsDone = false;
        this.st(patch);
        this.later(() => this.scrollTo('[data-c-step="' + idx + '"]'), 40);
      },
      'c-sum-edit': () => this.st({ cEdit: S.cEdit === el.dataset.k ? null : el.dataset.k, ceName: S.nameText }),
      'c-ce-charms': () => {
        const prev = S.charmCount || 1;
        S.charmCount = Math.max(1, Math.min(this.cfg.maxCharms || 4, +el.dataset.n || 1));
        this.syncNames(S);
        if (S.cAdded) S.cDirty = true;
        // more charms: open the names right away, the new ones are empty
        S.cEdit = S.charmCount > prev ? 'name' : null;
        this.save(); this.render();
      },
      'c-charms-go': () => this.answer(),
      'c-bce-charms': () => { const m = S.mf; const prev = m.charmCount || 1; m.charmCount = Math.max(1, Math.min(this.ctxMaxCharms(), +el.dataset.n || 1)); this.syncNames(m); m.designSeq = null; S.bEdit = m.charmCount > prev ? 'name' : null; this.save(); this.render(); },
      'c-pce-charms': () => {
        const idx = +el.dataset.p; const pc = S.pieces[idx]; if (!pc) return;
        const mx = (this.pieceTarget(pc) || {}).maxCharms || 4;
        const c = Object.assign({}, pc.cfg); const prev = c.charmCount || 1;
        c.charmCount = Math.max(1, Math.min(mx, +el.dataset.n || 1)); this.syncNames(c);
        const pieces = S.pieces.slice(); pieces[idx] = Object.assign({}, pc, { cfg: c, dirty: true });
        this.st({ pieces, pEdit: c.charmCount > prev ? { idx, key: 'name' } : null });
      },
      'c-ce-finish': () => this.sumSet({ finish: el.dataset.id }),
      'c-ce-length': () => this.sumSet({ length: +el.dataset.n }),
      'c-ce-cord': () => this.sumSet({ cord: el.dataset.id }),
      'c-ce-style': () => this.sumSet({ style: el.dataset.id }),
      'c-ce-name-save': () => this.saveCeName(),
      'c-ce-scratch': () => this.sumSet({ scratch: !S.scratch }, true),
      'c-ce-gift': () => this.sumSet({ gift: !S.gift }, true),
      'c-continue-summary': () => this.continueSummary(),
      'c-scroll-summary': () => this.scrollTo('[data-c-summary]'),
      'c-start-mf': () => { const o = (this.cfg.offers || [])[i]; if (o && !this.mfMode()) this.startMf(o); },
      'c-b-continue': () => this.advanceBNext(),
      'c-b-name': () => this.sendBName(),
      'c-b-use-design': () => {
        const d = this.pieceDesigns()[i];
        if (d) { Object.assign(S.mf, { nameText: d.nameText, pattern: d.pattern, style: d.style, designSeq: d.designSeq }); this.advanceBNext(); }
      },
      'c-b-cancel': () => this.cancelMf(),
      'c-b-edit-step': () => { const idx = +el.dataset.step; this.st({ bReopen: idx, bEdit: null }); this.later(() => this.scrollTo('[data-c-bstep="' + idx + '"]'), 40); },
      'c-b-edit': () => this.st({ bEdit: S.bEdit === el.dataset.k ? null : el.dataset.k, bceName: (S.mf || {}).nameText || '' }),
      'c-bce-finish': () => this.bSet({ finish: el.dataset.id }),
      'c-bce-length': () => this.bSet({ length: +el.dataset.n }),
      'c-bce-cord': () => this.bSet({ cord: el.dataset.id }),
      'c-bce-style': () => this.bSet({ style: el.dataset.id }),
      'c-bce-name-save': () => { const t = (S.bceName || '').trim(); if (t) Object.assign(S.mf, { nameText: t, designSeq: null }); this.st({ bEdit: null }); },
      'c-bce-scratch': () => this.bSet({ scratch: !S.mf.scratch }, true),
      'c-bce-gift': () => this.bSet({ gift: !S.mf.gift }, true),
      'c-go-cart': () => { this.save(); window.location.href = '/cart'; },
      'c-remove-piece': () => this.removePiece(i),
      'c-scroll-piece': () => this.scrollTo('[data-c-piece="' + i + '"]'),
      'c-p-edit': () => { const idx = +el.dataset.p; if (!S.pieces[idx]) return; const same = S.pEdit && S.pEdit.idx === idx && S.pEdit.key === el.dataset.k; this.st({ pEdit: same ? null : { idx, key: el.dataset.k }, pceName: (S.pieces[idx].cfg.nameText || '') }); },
      'c-pce-finish': () => this.pSet(+el.dataset.p, { finish: el.dataset.id }),
      'c-pce-length': () => this.pSet(+el.dataset.p, { length: +el.dataset.n }),
      'c-pce-cord': () => this.pSet(+el.dataset.p, { cord: el.dataset.id }),
      'c-pce-style': () => this.pSet(+el.dataset.p, { style: el.dataset.id }),
      'c-pce-name-save': () => { const idx = +el.dataset.p; const t = (S.pceName || '').trim(); if (t && !this.charBad(t) && !this.tooManyNames(t) && !this.typeWord(t)) this.pSet(idx, { nameText: t }); else this.st({ pEdit: null }); },
      'c-pce-scratch': () => this.pSet(+el.dataset.p, { scratch: !S.pieces[+el.dataset.p].cfg.scratch }, true),
      'c-pce-gift': () => this.pSet(+el.dataset.p, { gift: !S.pieces[+el.dataset.p].cfg.gift }, true),
      'pstone-inc': () => {
        if (!S.pEdit) return; const pc = S.pieces[S.pEdit.idx]; const c = pc.cfg;
        const mx = (this.pieceTarget(pc) || {}).maxStones || 6;
        if (this.stoneCount(c) >= mx) { this.showToast('Up to ' + mx + ' stones per piece.'); return; }
        const ns = Object.assign({}, c.stones); ns[i] = (ns[i] || 0) + 1; this.pSet(S.pEdit.idx, { stones: ns }, true);
      },
      'pstone-dec': () => {
        if (!S.pEdit) return; const c = S.pieces[S.pEdit.idx].cfg;
        if (!c.stones || !c.stones[i]) return;
        const ns = Object.assign({}, c.stones); if (ns[i] <= 1) delete ns[i]; else ns[i] -= 1; this.pSet(S.pEdit.idx, { stones: ns }, true);
      },
      'c-p-update': () => this.updatePiece(+el.dataset.p),
    };
    if (mine[a]) { mine[a](); return; }
    super.onClick(e);
  }
  sumSet(patch, keepOpen) {
    const S = this.state;
    Object.assign(S, patch);
    if (S.cAdded) S.cDirty = true;
    if (!keepOpen) S.cEdit = null;
    this.save(); this.render();
  }
  bSet(patch, keepOpen) {
    Object.assign(this.state.mf, patch);
    if (!keepOpen) this.state.bEdit = null;
    this.save(); this.render();
  }
  onCharmName(key) { if (key === 'cname' && this.state.cAdded) this.state.cDirty = true; }
  charmChips(o, action, pIdx, maxCharms) {
    const n = o.charmCount || 1, max = Math.max(1, maxCharms || this.ctxMaxCharms()), ch = this.cfg.charms || {};
    const out = [];
    for (let k = 1; k <= max; k++) {
      const pr = (ch[String(k)] || {}).price || 0;
      out.push(`<button ${this.chipB(n === k)} data-action="${action}" data-n="${k}"${pIdx != null ? ` data-p="${pIdx}"` : ''}>${k} ${k > 1 ? 'charms' : 'charm'}${pr ? ' · +' + this.fmt(pr) : ''}</button>`);
    }
    return out.join('');
  }
  saveCeName() {
    const t = (this.state.ceName || '').trim();
    if (t && !this.charBad(t) && !this.tooManyNames(t) && !this.typeWord(t)) this.sumSet({ nameText: t });
    else this.st({ cEdit: null });
  }
  onInput(e) {
    const el = e.target;
    if (!el.dataset || !el.dataset.input) return;
    if (el.dataset.input === 'ce-name') { this.state.ceName = el.value; return; }
    if (el.dataset.input === 'bce-name') { this.state.bceName = el.value; return; }
    if (el.dataset.input === 'pce-name') { this.state.pceName = el.value; return; }
    if (el.dataset.input === 'pce-cname') {
      // charm names of a piece already in the set: edited in place (no re-render, keeps focus)
      const S = this.state; if (!S.pEdit) return;
      const pc = S.pieces[S.pEdit.idx]; if (!pc) return;
      const c = Object.assign({}, pc.cfg); c.names = (c.names || []).slice(); c.names[+el.dataset.k || 0] = el.value; this.syncNames(c);
      S.pieces = S.pieces.slice(); S.pieces[S.pEdit.idx] = Object.assign({}, pc, { cfg: c, dirty: true });
      el.classList.toggle('cfg__input--invalid', !!el.value.trim() && !this.charmNameOk(el.value));
      this.save();
      return;
    }
    super.onInput(e);
    const send = this.root.querySelector('[data-c-send]');
    if (send) send.disabled = this.mfMode() ? !this.mfNameValid() : !this.nameValid();
  }

  /* ---------- scroll diagnostics (?chatdebug=1) ---------- */
  dbgOn() { if (this._dbgOn == null) this._dbgOn = /chatdebug/.test(location.search); return this._dbgOn; }
  dbg(msg) {
    if (!this.dbgOn()) return;
    const el = this.feedEl();
    const line = Math.round(performance.now() / 100) / 10 + 's ' + msg + (el ? ' [top ' + Math.round(el.scrollTop) + ' end ' + Math.max(0, el.scrollHeight - el.clientHeight) + ' ch ' + el.clientHeight + ']' : '');
    this._dbgLines = (this._dbgLines || []).concat(line).slice(-12);
    let box = document.getElementById('cht-dbg');
    if (!box) {
      box = document.createElement('pre'); box.id = 'cht-dbg';
      box.style.cssText = 'position:fixed;left:0;right:0;bottom:0;z-index:2147483647;margin:0;padding:6px 8px;background:rgba(0,0,0,.82);color:#9f9;font:10px/1.35 monospace;white-space:pre-wrap;pointer-events:none;';
      document.body.appendChild(box);
    }
    box.textContent = this._dbgLines.join('\n');
  }
  dbgHook(el) {
    if (!this.dbgOn() || !el || el._dbgHooked) return;
    el._dbgHooked = true;
    const desc = Object.getOwnPropertyDescriptor(Element.prototype, 'scrollTop');
    const self = this;
    Object.defineProperty(el, 'scrollTop', {
      configurable: true,
      get() { return desc.get.call(this); },
      set(v) { const who = (new Error().stack || '').split('\n').slice(1, 4).map((l) => l.trim().replace(/^at /, '').replace(/\(.*\)/, '').split('@')[0]).join('<'); self.dbg('set ' + Math.round(v) + ' by ' + who); desc.set.call(this, v); }
    });
    el.addEventListener('scroll', () => self.dbg('scroll event'), { passive: true });
  }

  /* ---------- scrolling ---------- */
  feedEl() { return this.root.querySelector('.cht__feed'); }
  scrollFeed(smooth) {
    const el = this.feedEl(); if (!el) return;
    const firstNew = this._firstNew && el.contains(this._firstNew) ? this._firstNew : null;
    this._lastScrollNew = firstNew ? (firstNew.className.split(' ')[0] + ':' + (firstNew.textContent || '').trim().slice(0, 30)) : null;
    this._firstNew = null; this._firstNewSig = null;
    // The view starts at the designer's first new message of this turn (the
    // text that tells the shopper what to do), with the choices right below
    // it. A user answer never anchors on its own: the bot row that follows it
    // does. Anything loading further down (photos, cards) sits below the
    // anchor, so it can never push the message away.
    const nextBot = (from) => {
      const all = [...el.querySelectorAll('.cht__row, .cht__widget, .cht__sum')];
      let i = from ? all.indexOf(from) : -1;
      for (i = i + 1; i < all.length; i++) {
        const n = all[i];
        if (n.classList.contains('cht__row--user')) return null; // next turn: stop
        if (n.classList.contains('cht__row--bot') && !n.classList.contains('cht__row--typing')) return n;
      }
      return null;
    };
    // One anchor per turn: the shopper's tap opens a turn, the first new
    // element after it fixes the anchor, and the designer's follow-up bubbles
    // (typed in by timers, no tap in between) keep it, so the view never jumps
    // to the second bubble and hides the first.
    const bySig = (sig) => sig ? [...el.querySelectorAll('.cht__row, .cht__widget, .cht__sum')].find((n) => this.sigOf(n) === sig) || null : null;
    let anchor = bySig(this._turnAnchorSig);
    if (firstNew && (this._turnOpen || !anchor)) {
      anchor = firstNew.classList.contains('cht__row--user') ? (nextBot(firstNew) || firstNew) : firstNew;
      this._turnAnchorSig = this.sigOf(anchor);
      this._turnOpen = false;
    }
    const target = () => {
      const end = Math.max(0, el.scrollHeight - el.clientHeight);
      const rows = el.querySelectorAll('.cht__row--user:not(.cht__row--reply)');
      const lastUser = rows[rows.length - 1];
      let a = anchor && anchor.isConnected ? anchor : null;
      if (a && a.classList.contains('cht__row--user')) a = nextBot(a) || a;
      if (!a && lastUser) a = nextBot(lastUser) || lastUser;
      // Measured against the feed itself: offsetTop would count from the
      // dialog and clip the first line of the message.
      if (a) return Math.max(0, Math.min(a.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - 14, end));
      return end;
    };
    this.dbg('scrollFeed step ' + this.state.cStep + ' target ' + Math.round(target()) + (firstNew ? ' new ' + firstNew.className.split(' ').pop() : ''));
    el.scrollTop = target();
    this.holdAnchor(el, target, firstNew, smooth);
  }
  /* Keeps the feed on its target while the new block settles (photos load,
     the folded widget finishes collapsing, fonts swap), so late growth can't
     leave the view at the end of the message. Stops as soon as the shopper
     scrolls or taps, on the next scroll request, or after a few seconds. */
  holdAnchor(el, target, firstNew, smooth) {
    if (this._stopHold) this._stopHold();
    const t0 = performance.now();
    let stopped = false;
    const apply = () => {
      if (stopped) return;
      if (!el.isConnected || (firstNew && !firstNew.isConnected)) { stop(); return; }
      el.scrollTop = target();
    };
    const HOLD = 8000;
    const ro = window.ResizeObserver ? new ResizeObserver(() => { if (performance.now() - t0 > HOLD) { stop(); return; } apply(); }) : null;
    const onUser = (e) => { this.dbg('hold stop: ' + e.type); stop(); };
    // A photo that finishes loading (even on a slow connection) re-applies
    // the target on the next frame, after the browser has laid it out.
    const onImg = () => { if (performance.now() - t0 > HOLD) { stop(); return; } requestAnimationFrame(apply); };
    // Frame-independent fallback: re-apply whenever the feed's size changes.
    let lastSize = el.scrollHeight + ':' + el.clientHeight;
    const poll = setInterval(() => {
      const size = el.scrollHeight + ':' + el.clientHeight;
      if (size !== lastSize) { lastSize = size; apply(); }
    }, 120);
    const stop = () => {
      if (stopped) return;
      stopped = true;
      clearInterval(poll);
      if (ro) ro.disconnect();
      ['touchstart', 'wheel', 'pointerdown'].forEach((t) => el.removeEventListener(t, onUser));
      el.removeEventListener('load', onImg, true);
      if (this._stopHold === stop) this._stopHold = null;
    };
    this._stopHold = stop;
    ['touchstart', 'wheel', 'pointerdown'].forEach((t) => el.addEventListener(t, onUser, { passive: true }));
    el.addEventListener('load', onImg, true);
    if (ro) { ro.observe(el); Array.from(el.children).forEach((c) => ro.observe(c)); }
    const track = () => { if (stopped) return; apply(); if (performance.now() - t0 < 700) requestAnimationFrame(track); };
    if (smooth) requestAnimationFrame(track);
    setTimeout(stop, HOLD + 200);
  }
  scrollTo(sel) {
    const el = this.feedEl(); if (!el) return;
    const box = el.querySelector(sel); if (!box) return;
    const top = box.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - 12;
    el.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  /* ---------- render ---------- */
  render(toEnd) {
    if (!this.hasAttribute('open')) return;
    const S = this.state;
    if (!this.root.querySelector('.cht__dialog')) {
      this.root.innerHTML = `
        <div class="cfg__backdrop" data-action="backdrop"></div>
        <div class="cfg__dialog cht__dialog" role="dialog" aria-modal="true" aria-label="Customize your ${esc(this.cfg.product.title)} with your designer"></div>`;
    }
    const dialog = this.root.querySelector('.cht__dialog');
    const prevFeed = dialog.querySelector('.cht__feed');
    const keepTop = prevFeed ? prevFeed.scrollTop : 0;
    const keepRows = [];
    dialog.querySelectorAll('.cfg__stones-row, .cht__cards').forEach((r) => keepRows.push(r.scrollLeft));
    const active = document.activeElement;
    const focusKey = active && this.contains(active) && active.dataset && active.dataset.input ? active.dataset.input : null;
    const focusPos = focusKey ? active.selectionStart : null;
    const prevSheetOpen = !!dialog.querySelector('.cfg__sheet-backdrop');
    dialog.innerHTML = `
      ${S.confetti ? this.confettiHtml() : ''}
      ${this.chatHeaderHtml()}
      <div class="cht__feed" data-region="feed">${this.feedHtml()}<div class="cht__feed-end"></div></div>
      ${this.sheetsHtml()}
      ${S.toast ? `<div class="cfg__toast-wrap"><div class="cfg__toast"><span>${esc(S.toast.msg)}</span>${S.toast.undo ? '<button class="cfg__toast-undo" data-action="toast-undo">Undo</button>' : ''}</div></div>` : ''}`;
    const feed = dialog.querySelector('.cht__feed');
    this.dbgHook(feed);
    if (feed) feed.scrollTop = keepTop;
    // Entry animation only for elements that are new since the last render
    // (identified by a stable signature, so removed typing dots or folded
    // widgets never shift the bookkeeping). Everything already on screen
    // keeps still; the first new element is remembered for the scroll.
    const prevSigs = this._sigs || new Set();
    const sigs = new Set();
    let fresh = null;
    let pinned = null;
    dialog.querySelectorAll('.cht__row, .cht__widget, .cht__sum').forEach((el) => {
      const sig = this.sigOf(el);
      sigs.add(sig);
      if (this._firstNewSig && sig === this._firstNewSig && !pinned) pinned = el;
      if (prevSigs.has(sig)) el.classList.add('cht__settled');
      else if (!fresh && !el.classList.contains('cht__row--typing')) fresh = el;
    });
    this._sigs = sigs;
    // The first new element is remembered by signature (the DOM is rebuilt on
    // every render) until a scroll consumes it.
    if (!pinned && fresh) { this._firstNewSig = this.sigOf(fresh); pinned = fresh; }
    this._firstNew = pinned;
    dialog.querySelectorAll('.cfg__stones-row, .cht__cards').forEach((r, i) => { if (keepRows[i] != null) r.scrollLeft = keepRows[i]; });
    if (focusKey) {
      const inp = dialog.querySelector(`[data-input="${focusKey}"]`);
      if (inp) { inp.focus({ preventScroll: true }); try { if (focusPos != null) inp.setSelectionRange(focusPos, focusPos); } catch (err) { /* ignore */ } }
    }
    if (prevSheetOpen) {
      const bd = dialog.querySelector('.cfg__sheet-backdrop'); const sh = dialog.querySelector('.cfg__sheet');
      if (bd) bd.classList.add('cfg__sheet-backdrop--settled');
      if (sh) sh.classList.add('cfg__sheet--settled');
    }
    if (toEnd) this.scrollFeed(true);
    const cards = dialog.querySelector('.cht__cards');
    if (cards && this.mfMode()) requestAnimationFrame(() => cards.scrollTo({ left: cards.scrollWidth, behavior: 'smooth' }));
  }

  /* Stable identity of a chat element across re-renders: its step, its kind
     and (for bubbles) its text; widgets and cards ignore their live text so
     interacting inside them never counts as "new". */
  sigOf(el) {
    const step = el.closest('[data-c-step],[data-c-bstep]');
    const stepKey = step ? (step.dataset.cStep != null ? 's' + step.dataset.cStep : 'b' + step.dataset.cBstep) : 'x';
    // base class only: state modifiers (--dim, --settled, --typing) must not
    // make an existing element look new
    const kind = el.className.split(' ').find((c) => c.indexOf('cht__') === 0 && c.indexOf('--') === -1) || '';
    if (el.classList.contains('cht__row--typing')) return stepKey + '|typing';
    if (el.classList.contains('cht__row')) return stepKey + '|' + kind + '|' + (el.textContent || '').trim().slice(0, 80);
    const piece = el.getAttribute('data-c-piece');
    return stepKey + '|' + kind + '|' + (piece != null ? 'p' + piece : '');
  }

  /* ---------- header: piece cards + total ---------- */
  chatTotal() {
    const S = this.state;
    const pieceBase = (p) => { const o = p.offerHandle ? this.offerOf(p.offerHandle) : null; return o && o.dealPrice != null ? o.dealPrice : p.base; };
    if (S.pieces.length) {
      let t = S.pieces.reduce((a, p, i) => {
        const cfgP = (i === 0 && !p.offerHandle && S.cDirty) ? this.mainCfg() : p.cfg;
        return a + pieceBase(p) + this.addonPrice(cfgP, { stonePrice: p.stonePrice });
      }, 0);
      if (this.mfMode() && S.mf) { const o = this.offerOf(S.mfOffer) || {}; t += (o.dealPrice != null ? o.dealPrice : (o.price || 0)) + this.addonPrice(S.mf, { stonePrice: o.stonePrice }); }
      return t;
    }
    return this.cfg.product.price + this.addonPrice(S);
  }
  descOf(cfg, ctx) {
    const parts = [ctx.title];
    if (ctx.multi && cfg.charmCount) parts.push(this.charmsLabel(cfg));
    if (cfg.length) parts.push(cfg.length + '″');
    if (ctx.hasCord && cfg.cord) parts.push(this.cordOf(cfg.cord, ctx.cords).name + ' cord');
    if (cfg.finish) parts.push(this.finishOf(cfg.finish).name);
    const st = this.styleOf(cfg.style); if (st) parts.push(st.name);
    return parts.join(' · ');
  }
  extrasOf(cfg, t) {
    const parts = [];
    const n = this.stoneCount(cfg); if (n) parts.push(this.stonesLabel(cfg, t));
    if (cfg.scratch) parts.push('Scratch card');
    if (cfg.gift) parts.push('Gift box');
    return parts.length ? parts.join(' · ') : 'No extras';
  }
  chatHeaderHtml() {
    const S = this.state;
    const mf = this.mfMode();
    const mainCtx = { title: this.cfg.product.title, hasCord: this.cfg.hasCord, cords: this.cfg.cords, multi: !!this.cfg.multiCharm };
    const cards = [];
    const mainCfgNow = this.mainCfg();
    // Header photo follows the selection with the same cascade as the steps:
    // cord+finish combo -> finish file -> hero (404s fall through).
    const mainCands = [];
    if (this.cfg.hasCord && S.cord) mainCands.push(...this.comboCands(this.cfg.cordCombos, S.cord, S.finish));
    if ((this.cfg.images.finish || {})[S.finish]) mainCands.push(this.cfg.images.finish[S.finish]);
    if (this.cfg.images.hero) mainCands.push(this.cfg.images.hero);
    const mainImg = mainCands;
    const seq = this.chatSteps();
    const reached = (id) => S.cStep >= seq.indexOf(id) || S.cReopen != null;
    const liveCfg = Object.assign({}, mainCfgNow, {
      finish: reached('material') ? mainCfgNow.finish : null,
      length: reached('length') ? mainCfgNow.length : null,
      cord: reached('cord') ? mainCfgNow.cord : null,
    });
    cards.push({
      cands: mainImg, mono: S.nameText.trim() ? this.namesDisplayOf(S.nameText) : 'Your monogram', ghost: !S.nameText.trim(),
      desc: this.descOf(liveCfg, mainCtx), extras: this.extrasOf(mainCfgNow, null), live: !mf, editable: S.cStep >= this.stepIdx('summary') && !mf,
    });
    S.pieces.forEach((p, i) => {
      if (!p.offerHandle) return;
      const o = this.offerOf(p.offerHandle) || {};
      cards.push({ img: o.image, mono: this.namesDisplayOf(p.cfg.nameText), desc: this.descOf(p.cfg, { title: o.title || p.kind, hasCord: o.hasCord, cords: o.cords, multi: !!o.multiCharm }), extras: this.extrasOf(p.cfg, o), live: false, editable: !mf, pieceIdx: i });
    });
    if (mf && S.mf) {
      const o = this.offerOf(S.mfOffer) || {};
      cards.push({ img: o.image, mono: (S.mf.nameText || '').trim() ? this.namesDisplayOf(S.mf.nameText) : 'Your monogram', ghost: !(S.mf.nameText || '').trim(), desc: this.descOf(S.mf, { title: o.title, hasCord: o.hasCord, cords: o.cords, multi: !!o.multiCharm }), extras: this.extrasOf(S.mf, o), live: true, editable: false });
    }
    const kicker = mf ? 'YOUR SET · PIECE ' + cards.length : (cards.length > 1 ? 'YOUR SET · ' + cards.length + ' PIECES' : 'YOUR PIECE · LIVE');
    const cardHtml = cards.map((c) => `
      <div class="cht__card${c.live ? ' cht__card--live' : ''}${cards.length > 1 ? ' cht__card--multi' : ''}">
        <div class="cht__card-img">${c.cands ? this.cascadeImgHtml(c.cands, '', '') : (c.img ? `<img src="${esc(c.img)}" alt="">` : '')}</div>
        <div class="cht__card-body">
          <span class="cht__card-mono cfg-serif"${c.ghost ? ' style="opacity:.45"' : ''}>${esc(c.mono)}</span>
          <span class="cht__card-desc">${esc(c.desc)}</span>
          <span class="cht__card-extras">${esc(c.extras)}</span>
        </div>
        ${c.editable ? (c.pieceIdx != null ? `<button class="cht__card-edit" data-action="c-scroll-piece" data-i="${c.pieceIdx}">Edit</button>` : `<button class="cht__card-edit" data-action="c-scroll-summary">Edit</button>`) : ''}
        ${c.live && mf ? '<span class="cht__card-live">LIVE</span>' : ''}
      </div>`).join('');
    return `<div class="cht__header">
      <div class="cht__bar">
        <div class="cht__bar-who">
          ${this.avatarHtml('cht__bar-avatar')}
          <span class="cht__bar-name">${esc(this.designer().name)}</span>
          <span class="cht__bar-role">· your designer</span>
        </div>
        <button class="cfg__icon-btn" data-action="close" aria-label="Close"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg></button>
      </div>
      <div class="cht__cards">${cardHtml}</div>
      <div class="cht__total-row"><span class="cht__kicker">${kicker}</span><span class="cht__total cfg-serif">${this.fmt(this.chatTotal())}</span></div>
      ${this.campHowHtml()}
    </div>`;
  }
  /* Top line under the cards: the campaign's two parts while one is active,
     otherwise the usual two promises. */
  campHowHtml() {
    const c = this.campaign();
    if (!c || (!c.chatTop1 && !c.chatTop2)) return '<div class="cht__how"><span>✓ Preview in 24 to 72 hours</span><span>✓ Unlimited revisions or a full refund</span></div>';
    const dark = this.campDark(c);
    return `<div class="cht__how cfg-cm-how ${this.campLookCls(c)}">
      ${c.chatTop1 ? `<span class="cfg-cm-how__lead">${this.campSparkSvg(11)} ${this.campText(c.chatTop1, c)}</span>` : ''}
      ${c.chatTop2 ? `<span>${dark ? '' : '✓ '}${this.campText(c.chatTop2, c)}</span>` : ''}
    </div>`;
  }
  avatarHtml(cls) {
    const img = this.designerImg();
    const d = this.designer();
    return img ? `<img class="${cls}" src="${esc(img)}" alt="${esc(d.name)}">` : `<span class="${cls} cht__avatar--initial">${esc(d.name.slice(0, 1))}</span>`;
  }

  /* ---------- feed ---------- */
  bot(text, opts) {
    const o = opts || {};
    return `<div class="cht__row cht__row--bot${o.cont ? ' cht__row--cont' : ''}">
      ${o.cont ? '<span class="cht__avatar-spacer"></span>' : this.avatarHtml('cht__avatar')}
      <div class="cht__bubble cht__bubble--bot">${text}</div>
    </div>`;
  }
  user(text, stepIdx, visual, editAction) {
    return `<div class="cht__row cht__row--user">
      ${stepIdx != null ? `<button class="cht__edit" data-action="${editAction || 'c-edit'}" data-step="${stepIdx}" aria-label="Change this answer">✎</button>` : ''}
      <div class="cht__bubble cht__bubble--user">${visual ? `<span class="cht__ans-visual">${visual}</span>` : ''}<span>${text}</span></div>
    </div>`;
  }
  /* Small visual of what was chosen, carried inside the answer bubble */
  ansFinish(id) { const f = this.finishOf(id); return `<span class="cht__ans-dot" style="background:${f.grad}"></span>`; }
  ansCord(id, list) { const c = this.cordOf(id, list); return `<span class="cht__ans-dot" style="background:${c.c}"></span>`; }
  ansLength(L) { return `<span class="cht__ans-len cfg-serif">${esc(L)}</span>`; }
  ansName(text, finishId) { const f = this.finishOf(finishId); return `<span class="cht__ans-disc" style="background:${f.grad}"><span class="cfg-serif" style="color:${f.letter}">${esc(this.initialsOf(text))}</span></span>`; }
  ansStyle(id, finishId) { const st = this.styleOf(id); return st ? `<span class="cht__ans-thumb">${this.styleThumbHtml(st, finishId)}</span>` : ''; }
  ansStones(o, files, imgs, t) {
    const flat = [];
    Object.keys(o.stones || {}).sort((a, b) => a - b).forEach((k) => { for (let j = 0; j < o.stones[k]; j++) flat.push(+k); });
    if (!flat.length) return '';
    return `<span class="cht__ans-beads">${flat.slice(0, 6).map((mi) => {
      const meta = this.stoneMetaAt(mi, t);
      const src = this.stoneSrc(meta, files, imgs);
      return src ? `<img src="${esc(src)}" alt="${esc(meta.stone)}" data-src="${esc(src)}" onerror="this.onerror=null;(this.closest('configurator-chat-element')||{srcFailed(){}}).srcFailed(this.dataset.src)">` : `<span class="cht__ans-bead" style="background:${meta.c}"></span>`;
    }).join('')}</span>`;
  }
  ansGifts(o) {
    const th = (img, glyph) => `<span class="cht__ans-thumb cht__ans-thumb--photo">${img ? `<img src="${esc(img)}" alt="">` : glyph}</span>`;
    let out = '';
    if (o.scratch) out += th(this.cfg.scratch.image, '✦');
    if (o.gift) out += th(this.cfg.gift.image, '♡');
    return out;
  }
  widget(html, extra) { return `<div class="cht__widget${extra ? ' ' + extra : ''}">${html}</div>`; }
  typingHtml() {
    return `<div class="cht__row cht__row--bot cht__row--typing">${this.avatarHtml('cht__avatar')}<div class="cht__bubble cht__bubble--bot cht__typing"><span></span><span></span><span></span></div></div>`;
  }
  continueBtn(label, action, disabled) {
    return `<button class="cfg__btn-solid cht__continue" data-action="${action || 'c-continue'}"${disabled ? ' disabled' : ''}>${label}</button>`;
  }

  feedHtml() {
    const S = this.state;
    const steps = this.chatSteps();
    let out = '';
    steps.forEach((id, i) => {
      if (i > S.cStep) return;
      const open = (i === S.cStep && !S.cPending) || S.cReopen === i;
      const answered = (i < S.cStep || (i === S.cStep && S.cPending)) && S.cReopen !== i;
      out += `<div class="cht__step" data-c-step="${i}">${this.stepHtml(id, i, open, answered)}</div>`;
    });
    if (S.cTyping) out += this.typingHtml();
    return out;
  }

  stepHtml(id, i, open, answered) {
    const S = this.state;
    const dn = esc(this.designer().name);
    switch (id) {
      case 'intro': {
        const camp = this.campaign();
        if (this.newFlow) {
          const head = camp && camp.kicker ? esc(String(camp.kicker).split(' · ')[0]) : '';
          const strip = camp && camp.chatTip ? this.bot(`<span class="cfg-cm-bubble cfg-cm--light"><span>${this.campSparkSvg(11)} ${head ? head + ' · ' : ''}order today, arrives around ${this.campText('[arrival]', camp)}</span></span>`, { cont: true }) : '';
          return this.bot(this.rich("Hi, I'm {designer}. I'll draw your monogram by hand, and you'll see the draft by email in 24 to 72 hours.")) + strip;
        }
        const tip = camp && camp.chatTip ? this.bot(`<span class="cfg-cm-bubble cfg-cm--light">${camp.kicker ? `<span class="cfg-cm__kicker">${this.campSparkSvg(11)} ${this.campKicker(camp.kicker)}</span>` : ''}<span>${this.campText(camp.chatTip, camp)}</span></span>`, { cont: true }) : '';
        return this.bot(this.rich(this.opts.intro)) + tip;
      }
      case 'intro2': return this.bot(this.rich(this.opts.intro2), { cont: true })
        + (open ? `<div class="cht__row cht__row--user cht__row--reply"><button class="cht__reply" data-action="c-ready">Yes, I'm ready</button></div>` : '')
        + (answered ? this.user("Yes, I'm ready ✓", null) : '');
      case 'cord': return this.bot(`Let's start with the cord. Which color?`) + (open ? this.widget(this.cordWidget()) : '') + (answered ? this.user(esc(this.cordOf(S.cord, this.cfg.cords).name) + ' cord', i, this.ansCord(S.cord, this.cfg.cords)) : '');
      case 'material': return this.bot(this.cfg.hasCord ? 'Now the charm. Which finish?' : 'Which finish do we start with?') + (open ? this.widget(this.materialWidget()) : '') + (answered ? this.user(esc(this.finishOf(S.finish).name), i, this.ansFinish(S.finish)) : '');
      case 'charms': return this.bot(`Lovely. <b>How many charms</b> should it hold? Each one gets its own monogram.`) + (open ? this.widget(this.charmsWidget()) : '') + (answered ? this.user(esc(this.charmsLabel(S)), i, this.ansCharms(S)) : '');
      case 'length': return this.bot(`Lovely. How should it sit? Here's how each length falls:`) + (open ? this.widget(this.lengthWidget()) : '') + (answered ? this.user(this.lengthLabel(S.length), i, this.ansLength(S.length)) : '');
      case 'name': if (this.cfg.multiCharm) {
        const n = S.charmCount || 1;
        return this.bot(`Now my favourite part: <b>type the text for ${n > 1 ? 'each charm' : 'your charm'}</b>.<br><span class="cht__sub">Exactly as you want it: a name like David, two names like Sandy, Dale, or initials like B, L, P, T. Each one becomes its own monogram.</span>`) + (open ? this.widget(this.multiNameWidget(S, 'cname', 'c-send-name', this.nameValid())) : '') + (answered ? this.user('“' + esc(this.namesDisplayOf(S.nameText)) + '”', i, this.ansName(S.nameText, S.finish)) : '');
      }
      return this.bot(this.famCtx()
        ? `Now my favourite part: <b>type the initials</b> of your family.<br><span class="cht__sub">One letter for each name, for example B, L, P, T. I weave them into one monogram.</span>`
        : `Now my favourite part: <b>type the text</b> I turn into your monogram.<br><span class="cht__sub">Exactly as you want it: initials like B, L, P, T, a name like David, or a word like Love.</span>`) + (open ? this.widget(this.nameWidget()) : '') + (answered ? this.user('“' + esc(this.namesDisplayOf(S.nameText)) + '”', i, this.ansName(S.nameText, S.finish)) : '');
      case 'style': {
        const q = (S.nameText.trim() ? '“' + esc(this.namesDisplayOf(S.nameText)) + '”, I love it already.' : 'Perfect.') + ' Which design style would you like?';
        return this.bot(q) + (open ? this.widget(this.styleWidget(), 'cht__widget--flush') : '') + (answered ? this.user(esc((this.styleOf(S.style) || {}).name || ''), i, this.ansStyle(S.style, S.finish)) : '');
      }
      case 'stones': {
        const done = S.cExtrasDone && !open;
        return this.bot(`Now let's give it <b>more meaning</b>. Add the birthstones of the people you love: each month tells its own story:`) + (open ? this.widget(this.stonesWidget()) : '') + (done ? this.user(this.stoneCount(S) ? 'Birthstones: ' + esc(this.stonesLabel(S, null)) : 'No birthstones, thanks', i, this.ansStones(S, this.cfg.stoneFiles, this.cfg.stoneImages, null)) : '');
      }
      case 'gifts': {
        const done = S.cGiftsDone && !open;
        return this.bot(`Almost done. Shall I make it <b>gift-ready</b>? A little surprise inside, and a box ready to hand over:`) + (open ? this.widget(this.giftsWidget()) : '') + (done ? this.user(this.giftsReply(S), i, this.ansGifts(S)) : '');
      }
      case 'summary': return this.bot(`Here it is. My pencil's ready: your <b>first sketch lands within 24 to 72 hours</b>, and nothing is produced without your approval.`) + this.summaryCard();
      case 'deal': return this.dealHtmlChat();
    }
    return '';
  }
  lengthLabel(L) { return esc(L) + ' in · ' + Math.round(L * 2.5) + ' cm'; }
  giftsReply(o) {
    const g = []; if (o.scratch) g.push('Scratch & Reveal Card'); if (o.gift) g.push('Gift Box');
    return g.length ? 'Gift-ready: ' + esc(g.join(', ')) : 'No gift extras, thanks';
  }

  /* ---------- widgets (main + bundle share them through cur()) ---------- */
  heroFor() {
    const S = this.state, mf = this.mfMode(), o = this.cur();
    const offer = mf ? (this.offerOf(S.mfOffer) || {}) : null;
    const hasCord = mf ? !!offer.hasCord : this.cfg.hasCord;
    const finMap = mf ? (offer.finishImages || {}) : (this.cfg.images.finish || {});
    const fallback = mf ? offer.image : this.cfg.images.hero;
    const alt = mf ? (offer.title || '') : this.cfg.product.title;
    const cands = [];
    if (hasCord && o.cord) cands.push(...this.comboCands(mf ? offer.cordCombos : this.cfg.cordCombos, o.cord, o.finish));
    if (finMap[o.finish]) cands.push(finMap[o.finish]);
    if (fallback) cands.push(fallback);
    return `<div class="cht__hero">${this.cascadeImgHtml(cands, alt, 'Product photo')}</div>`;
  }
  cordWidget() {
    const S = this.state, mf = this.mfMode(), o = this.cur();
    const offer = mf ? (this.offerOf(S.mfOffer) || {}) : null;
    const cordImgs = mf ? offer.cordImages : this.cfg.images.cordImages;
    const fallback = mf ? offer.image : this.cfg.images.hero;
    const combos = mf ? offer.cordCombos : this.cfg.cordCombos;
    const cands = this.comboCands(combos, o.cord, 'black');
    if ((cordImgs || {})[o.cord]) cands.push(cordImgs[o.cord]);
    if (fallback) cands.push(fallback);
    const cards = this.cordsFor().map((cd) => {
      const sel = o.cord === cd.id;
      return `<button class="cfg__finish-card" data-action="select-cord" data-id="${cd.id}" data-selected="${sel}" aria-label="${cd.name} cord${sel ? ', selected' : ''}">
        <span class="cfg__finish-swatch cfg__finish-swatch--round" style="background:${cd.c}"></span>
        <span class="cfg__finish-name">${cd.name}</span>
      </button>`;
    }).join('');
    const cord = this.cordOf(o.cord);
    return `<div class="cht__hero">${this.cascadeImgHtml(cands, mf ? (offer.title || '') : this.cfg.product.title, 'Product photo')}</div>
      <div class="cfg__grid-2">${cards}</div>
      ${this.continueBtn('Continue with ' + esc(cord.name) + ' ✓', mf ? 'c-b-continue' : 'c-continue')}`;
  }
  materialWidget() {
    const S = this.state, mf = this.mfMode(), o = this.cur();
    const offer = mf ? (this.offerOf(S.mfOffer) || {}) : null;
    const hasCord = mf ? !!offer.hasCord : this.cfg.hasCord;
    const fin = this.finishOf(o.finish);
    return `${this.heroFor()}
      ${this.finishCardsHtml(o.finish)}
      ${hasCord && o.cord ? this.comboRecapHtml(o.cord, o.finish, mf ? offer.cords : this.cfg.cords) : '<div class="cfg__hint">Premium stainless steel. Won\'t fade or tarnish.</div>'}
      ${this.continueBtn('Continue with ' + esc(fin.name) + ' ✓', mf ? 'c-b-continue' : 'c-continue')}`;
  }
  lengthWidget() {
    const o = this.cur(), mf = this.mfMode();
    const LS = this.lengthsFor();
    const selDef = this.defaultLength(LS);
    const sel = o.length || selDef;
    const photo = this.lengthPhotoFor(o);
    const MARKS = { 16: 44.5, 18: 53.5, 20: 61.5, 22: 69.5 };
    const shot = photo ? `<div class="cfg__length-shot cht__length-shot">
        <div class="cfg__length-frame"><img src="${esc(photo)}" alt="Chain lengths on the neck" data-src="${esc(photo)}" onerror="this.onerror=null;(this.closest('configurator-chat-element')||{srcFailed(){}}).srcFailed(this.dataset.src)"></div>
        ${LS.map((L, li) => `<button class="cfg__length-mark" style="top:${LS.length === 4 ? MARKS[L] : 44.5 + 25 * (li / Math.max(1, LS.length - 1))}%" data-on="${sel === L}" data-action="select-length" data-n="${L}" aria-label="${L} inches">${L}″</button>`).join('')}
      </div>` : '';
    const notes = { 16: 'close to the neck', 18: 'classic everyday', 20: 'slightly relaxed', 22: 'long, statement' };
    const cards = LS.map((L) => `<button class="cht__len-card" data-action="select-length" data-n="${L}" data-selected="${sel === L}" aria-label="${L} inches${sel === L ? ', selected' : ''}">
        <span class="cht__len-main"><b>${L} in</b> <span>· ${Math.round(L * 2.5)} cm</span></span>
        <span class="cht__len-note">${notes[L] || ''}</span>
        ${L === 18 ? '<span class="cfg__pill-badge">Most chosen</span>' : ''}
      </button>`).join('');
    return `${shot}<div class="cht__len-grid">${cards}</div>
      ${this.continueBtn('Continue with ' + this.lengthLabel(sel) + ' ✓', mf ? 'c-b-continue' : 'c-continue')}`;
  }
  charmsWidget() {
    const o = this.cur(), mf = this.mfMode();
    const n = o.charmCount || 1, max = this.ctxMaxCharms(), ch = this.cfg.charms || {};
    const offer = mf ? (this.offerOf(this.state.mfOffer) || {}) : null;
    const img = (ch[String(n)] || {}).image || (mf ? offer.image : this.cfg.images.hero);
    const cards = [];
    for (let k = 1; k <= max; k++) {
      const pr = (ch[String(k)] || {}).price || 0;
      cards.push(`<button class="cht__len-card" data-action="select-charms" data-n="${k}" data-selected="${n === k}" aria-label="${k} ${k > 1 ? 'charms' : 'charm'}${n === k ? ', selected' : ''}">
        <span class="cht__len-main"><b>${k} ${k > 1 ? 'charms' : 'charm'}</b></span>
        <span class="cht__len-note">${pr ? '+' + this.fmt(pr) : 'Included'}</span>
        ${k === 2 ? '<span class="cfg__pill-badge cht__charm-pill">Most chosen</span>' : ''}
      </button>`);
    }
    return `<div class="cht__hero">${img ? `<img src="${esc(img)}" alt="Bracelet with ${n} ${n > 1 ? 'charms' : 'charm'}">` : ''}</div>
      <div class="cht__len-grid">${cards.join('')}</div>
      ${this.continueBtn('Continue with ' + esc(this.charmsLabel(o)) + ' ✓', mf ? 'c-b-continue' : 'c-charms-go')}`;
  }
  multiNameWidget(o, key, action, valid) {
    return `<div class="cht__charm-fields">${this.charmFieldsHtml(o, key)}</div>
      <button class="cfg__btn-solid cht__continue" data-action="${action}" data-c-send${valid ? '' : ' disabled'}>Create the monograms</button>
      <span class="cfg__hint cfg__hint--left cfg__hint--tiny">The designer adapts each text to fit its charm.</span>`;
  }
  ansCharms(o) { return `<span class="cht__ans-len cfg-serif">${esc(o.charmCount || 1)}</span>`; }
  nameWidget() {
    const S = this.state;
    const invalid = S.nameText.trim() && !this.nameValid();
    return `<div class="cht__input-row">
        <input class="cfg__input${invalid ? ' cfg__input--invalid' : ''}" data-input="name" value="${esc(S.nameText)}" maxlength="40" placeholder="${esc(this.namePlaceholder())}" autocomplete="off" enterkeyhint="send">
        <button class="cht__send" data-action="c-send-name" data-c-send${this.nameValid() ? '' : ' disabled'}>Create</button>
      </div>
      <div data-region="name-msgs" class="cht__msgs">${this.nameMsgsHtml()}</div>
      <span class="cfg__hint cfg__hint--left cfg__hint--tiny">The designer adapts your text to fit the monogram.</span>`;
  }
  styleWidget() {
    const o = this.cur();
    const act = this.mfMode() ? 'c-style' : 'c-style';
    return `<div class="cht__styles">` + STYLES.map((s) => {
      const sel = o.style === s.id;
      const useSvg = !!this.styleExUrls(s);
      return `<button class="cfg__style-card cht__style-card" data-action="${act}" data-id="${s.id}" data-selected="${sel}" aria-label="${s.name}, ${s.desc}">
        <div class="cfg__style-exs${useSvg ? ' cfg__style-exs--img' : ''}">${this.styleExHtml(s)}</div>
        <div class="cfg__style-meta"><span style="flex:1"><span class="cfg__style-name">${s.name}${s.badge ? ` <span class="cfg__pill-badge">${s.badge}</span>` : ''}</span><span class="cfg__style-desc">${s.desc}</span></span></div>
      </button>`;
    }).join('') + '</div>';
  }
  chainStageHtml(o) {
    const S = this.state, mf = this.mfMode();
    const offer = mf ? (this.offerOf(S.mfOffer) || {}) : null;
    const fin = this.finishOf(o.finish || S.finish);
    const maxS = this.maxStonesFor();
    const ctxTitle = ((mf ? offer.title : this.cfg.product.title) || '').toLowerCase();
    const ctxHandle = ((mf ? offer.handle : this.cfg.product.handle) || '').toLowerCase();
    const hasCord = mf ? !!offer.hasCord : this.cfg.hasCord;
    const hasLen = mf ? !!offer.hasLength : this.cfg.hasLength;
    const isNeck = hasLen && !hasCord;
    // charms (bag charm, pet charm, family initials charm) hang like a keychain
    const isBrac = ctxTitle.indexOf('bracelet') !== -1 || ctxHandle.indexOf('bracelet') !== -1;
    const isKeych = !isNeck && !isBrac && (ctxTitle.indexOf('keychain') !== -1 || ctxHandle.indexOf('keychain') !== -1 || ctxTitle.indexOf('charm') !== -1 || ctxHandle.indexOf('charm') !== -1);
    const isLine = !isNeck && !isKeych && (hasCord || ctxTitle.indexOf('bracelet') !== -1 || ctxHandle.indexOf('bracelet') !== -1);
    const beadFiles = (mf ? offer.stoneFiles : this.cfg.stoneFiles) || {};
    const beadFail = this._lpFail || new Set();
    const flat = [];
    Object.keys(o.stones || {}).sort((a, b) => a - b).forEach((k) => { for (let j = 0; j < o.stones[k]; j++) flat.push(+k); });
    const beadAt = (mi, x, y) => {
      const meta = this.stoneMetaAt(mi);
      const src = (beadFiles[meta.month] && !beadFail.has(beadFiles[meta.month])) ? beadFiles[meta.month] : meta.image;
      if (src) return `<img class="cfg__chain-bead" src="${esc(src)}" alt="${esc(meta.stone)}" style="left:${(x - 9).toFixed(1)}px;top:${(y - 17).toFixed(1)}px">`;
      return `<span class="cfg__chain-stone" style="left:${(x - 6.5).toFixed(1)}px;top:${(y - 6.5).toFixed(1)}px;background:${meta.c}"></span>`;
    };
    const names = mf ? (S.mf.nameText || S.nameText) : S.nameText;
    const lineColor = hasCord ? this.cordOf(o.cord, mf ? offer.cords : this.cfg.cords).c : fin.chain;
    const discHtml = (extra, style) => `<div class="cfg__chain-disc${extra || ''}" style="background:${fin.grad};${style || ''}"><span class="cfg-serif" style="color:${fin.letter}">${esc(this.initialsOf(names))}</span></div>`;
    const shown = flat.slice(0, Math.min(6, maxS));
    let inner = '';
    if (isKeych) {
      const ys = [44, 61, 78];
      inner = `<div class="cfg__chain-ring" style="border-color:${fin.chain}"></div><div class="cfg__chain-vline" style="background:${fin.chain}"></div>${shown.slice(0, 3).map((mi, i) => beadAt(mi, 140, ys[i])).join('')}${discHtml(' cfg__chain-disc--drop', 'top:88px;')}`;
    } else if (isLine && this.ctxMulti()) {
      const L = this.charmLine(o, fin);
      inner = `<div class="cfg__chain-hline" style="background:${lineColor}"></div>${shown.map((mi, i) => beadAt(mi, L.slots[i], 66)).join('')}${L.discs}`;
    } else if (isLine) {
      const offs = [-40, 40, -64, 64, -88, 88];
      inner = `<div class="cfg__chain-hline" style="background:${lineColor}"></div>${shown.map((mi, i) => beadAt(mi, 140 + offs[i], 66)).join('')}${discHtml('', 'top:49px;')}`;
    } else {
      const slots = [[1, 1], [1, -1], [2, 1], [2, -1], [3, 1], [3, -1]];
      inner = `<div class="cfg__chain-arc" style="border-color:${fin.chain}"></div>${shown.map((mi, i) => { const n = slots[i][0], side = slots[i][1]; const phi = 0.24 + 0.21 * (n - 1); return beadAt(mi, 140 + side * 120 * Math.sin(phi), -4 + 112 * Math.cos(phi)); }).join('')}${discHtml(' cfg__chain-disc--drop', '')}`;
    }
    const total = this.stoneCount(o);
    return `<div class="cfg__stones-stage cht__stage">
      <div class="cfg__chain-clip${isNeck ? ' cfg__chain-clip--neck' : ''}"><div class="cfg__chain-scale${isNeck ? ' cfg__chain-scale--neck' : ''}">${inner}</div></div>
      <span class="cfg__chain-caption">${total > 0 ? total + ' stone' + (total > 1 ? 's' : '') + ' on your ' + (hasCord ? 'cord' : 'chain') + ' · live preview' : 'Tap + below and stones appear on the ' + (hasCord ? 'cord' : 'chain')}</span>
    </div>`;
  }
  stonesWidget() {
    const S = this.state, mf = this.mfMode(), o = this.cur();
    const offer = mf ? (this.offerOf(S.mfOffer) || null) : null;
    const total = this.stoneCount(o);
    const maxS = this.maxStonesFor();
    const stonePrice = mf && offer ? offer.stonePrice : this.cfg.stonePrice;
    const im = this.cfg.images;
    const fail = this._lpFail || new Set();
    const ctxPhoto = mf ? (offer || {}).stonesPhoto : im.stonesPhoto;
    const ctxShared = mf ? (offer || {}).stonesPhotoShared : im.stonesPhotoShared;
    const custom = [ctxPhoto, ctxShared].filter((u) => u && !fail.has(u))[0] || null;
    const src = custom || (mf ? null : im.stonesExample);
    const photo = src ? `<div class="cfg__stones-photo"><img src="${esc(src)}" alt="Birthstone example"${custom ? ` data-src="${esc(custom)}" onerror="this.onerror=null;(this.closest('configurator-chat-element')||{srcFailed(){}}).srcFailed(this.dataset.src)"` : ''}></div>` : '';
    const mainHasExtras = this.stoneCount(S) > 0 || S.scratch || S.gift;
    const sameDesign = mf && ((S.mf.designSeq === S.designSeq) || ((S.mf.nameText || '').trim().toUpperCase() === S.nameText.trim().toUpperCase()));
    const copyCard = mf && S.extrasChoice === null && mainHasExtras && sameDesign ? `<div class="cfg__copy-extras">
        <span class="cfg__copy-extras-title">⟳ Same extras as your first piece?</span>
        <span class="cfg__copy-extras-sub">${esc(this.extrasOf(S, null))}</span>
        <div class="cfg__copy-extras-row"><button class="cfg__btn-solid" style="flex:1;height:40px" data-action="copy-extras">Yes, copy them</button><button class="cfg__btn-ghost" style="flex:1;height:40px" data-action="choose-extras">Choose</button></div>
      </div>` : '';
    return `${copyCard}
      <div class="cfg__section-label-row"><span class="cfg__section-label">BIRTHSTONES</span><span class="cfg__section-counter">${total} of ${maxS} added</span></div>
      <div class="cfg__stones-preview">${photo}${this.chainStageHtml(o)}</div>
      <div class="cfg__stones-row">${this.stoneCardsHtml(o, { stonePrice })}</div>
      ${this.continueBtn(total ? 'Done ✓' : 'Continue without', 'c-done-stones')}`;
  }
  giftsWidget() {
    const o = this.cur(), mf = this.mfMode();
    const row = (id, title, copy, price, cmp, img, on, badge) => `<div class="cht__gift" data-on="${on}">
        <button class="cht__gift-photo" data-action="extra-detail" data-id="${id}" aria-label="View ${title} details">${img ? `<img src="${esc(img)}" alt="">` : '✦'}${this.zoomIcoHtml()}</button>
        <div class="cht__gift-body">
          <span class="cht__gift-title">${title}${badge ? ' <span class="cfg__pill-badge">Popular</span>' : ''}</span>
          <span class="cht__gift-copy">${copy}</span>
          <span class="cfg__extra-price">${cmp ? `<s class="cfg__extra-was">${this.fmt(cmp)}</s> ` : ''}+${this.fmt(price)}</span>
        </div>
        <button class="cht__gift-btn" data-action="${id === 'scratch' ? 'toggle-scratch' : 'toggle-gift'}" data-on="${on}" aria-label="${on ? 'Remove' : 'Add'} ${title}">${on ? '✓' : '+'}</button>
      </div>`;
    const sc = this.cfg.scratch, gf = this.cfg.gift;
    return row('scratch', 'Scratch &amp; Reveal Card', 'The meaning of this monogram, hidden under a scratch layer.', sc.price, sc.comparePrice, sc.image, !!o.scratch, true)
      + row('gift', 'Gift Box', 'Rigid box with the meaning card inside. Ready to give.', gf.price, gf.comparePrice, gf.image, !!o.gift, false)
      + this.continueBtn(o.scratch || o.gift ? 'Done ✓' : 'Continue without', 'c-done-gifts');
  }

  /* ---------- summary card (main piece) ---------- */
  chipB(on) { return `class="cht__chip${on ? ' cht__chip--on' : ''}"`; }
  summaryCard() {
    const S = this.state;
    const k = S.cEdit;
    const rows = [];
    const row = (key, label, value, editor) => `<div class="cht__sum-row" data-action="c-sum-edit" data-k="${key}"><span class="cht__sum-label">${label}</span><span class="cht__sum-val">${value} <span class="cht__sum-pen">✎</span></span></div>${k === key ? `<div class="cht__sum-editor">${editor}</div>` : ''}`;
    if (this.cfg.hasCord) rows.push(row('cord', 'Cord', esc(this.cordOf(S.cord, this.cfg.cords).name), this.cordsFor().map((c) => `<button ${this.chipB(S.cord === c.id)} data-action="c-ce-cord" data-id="${c.id}"><span class="cht__chip-dot" style="background:${c.c}"></span>${c.name}</button>`).join('')));
    rows.push(row('finish', 'Finish', esc(this.finishOf(S.finish).name), this.finishList(this.cfg.finishIds).map((f) => `<button ${this.chipB(S.finish === f.id)} data-action="c-ce-finish" data-id="${f.id}"><span class="cht__chip-dot" style="background:${f.grad}"></span>${f.name}</button>`).join('')));
    if (this.cfg.hasLength) rows.push(row('length', 'Chain length', this.lengthLabel(S.length || this.defaultLength(this.cfg.lengths)), this.lengthsFor().map((L) => `<button ${this.chipB(S.length === L)} data-action="c-ce-length" data-n="${L}">${L} in · ${Math.round(L * 2.5)} cm</button>`).join('')));
    if (this.cfg.multiCharm) {
      rows.push(row('charms', 'Charms', esc(this.charmsLabel(S)), this.charmChips(S, 'c-ce-charms', null, this.cfg.maxCharms)));
      rows.push(row('name', 'Names', '“' + esc(this.namesDisplayOf(S.nameText)) + '”', `<div class="cht__charm-fields">${this.charmFieldsHtml(S, 'cname')}</div><button class="cht__send" data-action="c-sum-edit" data-k="name">Done</button>`));
    } else rows.push(row('name', 'Monogram', '“' + esc(this.namesDisplayOf(S.nameText)) + '”', `<div class="cht__input-row"><input class="cfg__input" data-input="ce-name" value="${esc(S.ceName || S.nameText)}" maxlength="40" autocomplete="off"><button class="cht__send" data-action="c-ce-name-save">Save</button></div>`));
    rows.push(row('style', 'Monogram style', esc((this.styleOf(S.style) || {}).name || '…'), STYLES.map((s) => `<button ${this.chipB(S.style === s.id)} data-action="c-ce-style" data-id="${s.id}">${this.styleThumbHtml(s, S.finish)}${s.name}</button>`).join('')));
    if (!this.cfg.hideStones) {
      const n = this.stoneCount(S);
      rows.push(row('stones', 'Birthstones', n ? esc(n + (n > 1 ? ' stones' : ' stone') + ' · +' + this.fmt(n * this.cfg.stonePrice)) : 'None', `<div class="cfg__stones-row" style="padding:2px;margin:0 -2px">${this.stoneCardsHtml(S, { mini: true, stoneFiles: this.cfg.stoneFiles, stoneImages: this.cfg.stoneImages, stones: null })}</div>`));
    }
    const gN = (S.scratch ? 1 : 0) + (S.gift ? 1 : 0);
    rows.push(row('gifts', 'Gift extras', gN ? esc(gN + (gN > 1 ? ' extras' : ' extra') + ' · +' + this.fmt((S.scratch ? this.cfg.scratch.price : 0) + (S.gift ? this.cfg.gift.price : 0))) : 'None',
      `<button ${this.chipB(!!S.scratch)} data-action="c-ce-scratch">${S.scratch ? '✓' : '+'} Scratch card · ${this.fmt(this.cfg.scratch.price)}</button><button ${this.chipB(!!S.gift)} data-action="c-ce-gift">${S.gift ? '✓' : '+'} Gift box · ${this.fmt(this.cfg.gift.price)}</button>`));
    const total = this.cfg.product.price + this.addonPrice(S);
    let cta;
    if (S.busy) cta = `<button class="cfg__btn-solid cht__continue" disabled>Adding…</button>`;
    else if (this.cfg.multiCharm && !this.nameValid()) cta = `<button class="cfg__btn-solid cht__continue" disabled>Add a name for each charm</button>`;
    else if (S.cartEdit) cta = `<button class="cfg__btn-solid cht__continue" data-action="c-continue-summary">Save changes · ${this.fmt(total)}</button>`;
    else if (!S.cAdded) cta = `<button class="cfg__btn-solid cht__continue" data-action="c-continue-summary">Continue · ${this.fmt(total)}</button>`;
    else if (S.cDirty) cta = `<button class="cfg__btn-solid cht__continue" data-action="c-continue-summary">Update my piece · ${this.fmt(total)}</button>`;
    else cta = `<div class="cfg__ok-note"><span class="cfg__ok-check">✓</span><span>In your cart · ${this.fmt(total)}</span></div>`;
    return `<div class="cht__sum" data-c-summary>
      <div class="cht__sum-head"><span class="cht__sum-title cfg-serif">${esc(this.cfg.product.title)} “${esc(this.namesDisplayOf(S.nameText))}”</span><span class="cht__sum-price">${this.fmt(this.cfg.product.price + this.charmExtra(S))}</span></div>
      <span class="cht__sum-kicker">TAP ANY DETAIL TO CHANGE IT</span>
      <div class="cht__sum-rows">${rows.join('')}</div>
      ${cta}
    </div>`;
  }

  /* ---------- deal (bundle offers) + bundle sub-chat ---------- */
  dealHtmlChat() {
    const S = this.state;
    const bundle = this.cfg.bundle;
    const offers = this.cfg.offers || [];
    const mf = this.mfMode();
    const addedOffers = S.pieces.filter((p) => p.offerHandle).length;
    let out = '';
    if (!offers.length) {
      return this.bot(`Added to your cart ✓ Once you order, I start right away: your design within 24 to 72 hours, unlimited revisions, and production only after your yes.`)
        + this.widget(`<button class="cfg__btn-solid cht__continue" data-action="c-go-cart">Go to cart · ${this.fmt(this.chatTotal())}</button>`);
    }
    const camp = this.campaign();
    // Dark looks: the dark bubble (kicker + deal) shows only here, at the upsell.
    if (camp && camp.chatDeal) out += this.bot(this.campDark(camp) ? `<span class="cfg-cm-bubble ${this.campLookCls(camp)} cfg-cm--shine">${camp.kicker ? `<span class="cfg-cm__kicker">${this.campSparkSvg(11)} ${this.campKicker(camp.kicker)}</span>` : ''}<span>${this.campText(camp.chatDeal, camp)}</span></span>` : this.campText(camp.chatDeal, camp));
    else out += this.bot(`🎁 <b>Bundle unlocked!</b> Add a second piece for <span class="cht__deal-tag"><b>${this.fmt(bundle.secondPrice)}</b><s>${this.fmt(this.cfg.product.price)}</s></span><br><span class="cht__sub">Its own name and finish. Discount applied in the cart.</span>`);
    const shown = camp ? this.campOfferIdx() : null;
    // The offers block is always presented fresh: every card is "+ Add",
    // the same type can be added again. Progress lives in the header cards
    // and in each piece's recap card above.
    const cards = offers.map((o, i) => {
      if (shown && shown.indexOf(i) === -1) return '';
      const active = mf && S.mfOffer === o.handle;
      return `<div class="cht__offer${active ? ' cht__offer--active' : ''}">
        <div class="cht__offer-img">${o.image ? `<img src="${esc(o.image)}" alt="${esc(o.title)}">` : ''}</div>
        <div class="cht__offer-body">
          <span class="cht__offer-title">${esc(o.title)}${i === 0 ? ' <span class="cfg__pill-badge">Popular</span>' : ''}</span>
          <span class="cht__offer-price"><b>${this.fmt(o.dealPrice)}</b> <s>${this.fmt(o.price)}</s> with bundle${this.campSaveHtml(o, camp)}</span>
        </div>
        <button class="cht__offer-btn" data-action="c-start-mf" data-i="${i}"${mf ? ' disabled' : ''}>${active ? '…' : '+ Add'}</button>
      </div>`;
    }).join('');
    const note = addedOffers ? `Your set: ${S.pieces.length} pieces, each with its own monogram. Add another, or go to the cart.` : `Optional: tap + Add and I'll customize that piece with you.`;
    // After a piece joins the set, the follow-up question comes first and the
    // offers + cart button are re-shown right under it (always at the bottom).
    // Every piece added to the set keeps its recap card in the chat, exactly
    // like the first piece: tappable rows, "In your cart", update when edited.
    S.pieces.forEach((p, i) => {
      if (!p.offerHandle) return;
      const o = this.offerOf(p.offerHandle) || {};
      out += this.user('Added to my set ✓') + this.bot(`Your <b>${esc(o.title || p.kind)}</b> is in your set:`) + this.pieceSummaryCard(i);
    });
    if (S.bFollowPending && !mf) return out;
    if (addedOffers) {
      const n = S.pieces.length;
      const msg = n === 2 ? `Your set of 2 is ready ✓ Most gift-givers stop here, but a third piece is still at the bundle price. One more, or the cart?` : `A set of ${n}: someone is very loved. Another piece at the bundle price, or shall we go to the cart?`;
      out += this.bot(msg);
    }
    out += this.widget(`${camp && !mf ? this.campPeopleHtml('cfg-cm-people--chat') : ''}<div class="cht__offers">${cards}</div>
      <span class="cht__note">${esc(note)}</span>
      <button class="cfg__btn-solid cht__continue" data-action="c-go-cart"${mf ? ' disabled' : ''}>Go to cart · ${this.fmt(this.chatTotal())}</button>`, mf ? 'cht__widget--dim' : '');
    if (mf && S.mf) out += this.bundleChatHtml();
    return out;
  }
  bundleChatHtml() {
    const S = this.state;
    const o = this.offerOf(S.mfOffer) || {};
    const steps = this.bSteps();
    const cur = S.bStep;
    let out = this.bot(`Wonderful, your <b>${esc(o.title || 'piece')}</b> gets a character of its own.`);
    steps.forEach((id, i) => {
      if (i > cur) return;
      const open = (i === cur && !S.bPending) || S.bReopen === i;
      const answered = (i < cur || (i === cur && S.bPending)) && S.bReopen !== i;
      out += `<div class="cht__step cht__step--b" data-c-bstep="${i}">${this.bStepHtml(id, i, open, answered, o)}</div>`;
    });
    return out;
  }
  bStepHtml(id, bi, open, answered, o) {
    const S = this.state, m = S.mf || {};
    const ub = (text, visual) => this.user(text, bi, visual, 'c-b-edit-step');
    switch (id) {
      case 'mf-charms': return this.bot(`How many charms on this one?`) + (open ? this.widget(this.charmsWidget()) : '') + (answered ? ub(esc(this.charmsLabel(m)), this.ansCharms(m)) : '');
      case 'mf-name': {
        if (o.multiCharm) {
          const w = `${this.multiNameWidget(m, 'mf-cname', 'c-b-name', this.mfNameValid())}<button class="cht__link" data-action="c-b-cancel">Not now, back to my set</button>`;
          return this.bot((m.charmCount || 1) > 1 ? 'A name for each charm?' : 'What name goes on the charm?') + (open ? this.widget(w) : '') + (answered ? ub('“' + esc(this.namesDisplayOf(m.nameText)) + '”', this.ansName(m.nameText, m.finish)) : '');
        }
        const designs = this.pieceDesigns();
        const chips = designs.map((d, i) => `<button class="cht__chip cht__chip--name" data-action="c-b-use-design" data-i="${i}">Use “${esc(this.namesDisplayOf(d.nameText))}” again</button>`).join('');
        const invalid = (m.nameText || '').trim() && !this.mfNameValid();
        const w = `${chips ? `<div class="cht__chips">${chips}</div>` : ''}
          <div class="cht__input-row"><input class="cfg__input${invalid ? ' cfg__input--invalid' : ''}" data-input="mf-name" value="${esc(m.nameText || '')}" maxlength="40" placeholder="${chips ? 'Or a new name…' : esc(this.namePlaceholder())}" autocomplete="off" enterkeyhint="send"><button class="cht__send" data-action="c-b-name" data-c-send${this.mfNameValid() ? '' : ' disabled'}>Create</button></div>
          <div data-region="name-msgs" class="cht__msgs">${this.nameMsgsHtml()}</div>
          <button class="cht__link" data-action="c-b-cancel">Not now, back to my set</button>`;
        return this.bot(designs.length ? 'Same monogram as your first piece, or a new one?' : (this.famCtx() ? 'Type the initials for this monogram, one letter for each name.' : 'Type the text for this monogram, exactly as you want it.')) + (open ? this.widget(w) : '') + (answered ? ub('“' + esc(this.namesDisplayOf(m.nameText)) + '”', this.ansName(m.nameText, m.finish)) : '');
      }
      case 'mf-style': return this.bot('And its character?') + (open ? this.widget(this.styleWidget(), 'cht__widget--flush') : '') + (answered ? ub(esc((this.styleOf(m.style) || {}).name || ''), this.ansStyle(m.style, m.finish)) : '');
      case 'mf-cord': return this.bot('Which cord color?') + (open ? this.widget(this.cordWidget()) : '') + (answered ? ub(esc(this.cordOf(m.cord, o.cords).name) + ' cord', this.ansCord(m.cord, o.cords)) : '');
      case 'mf-finish': return this.bot(o.hasCord ? 'And the charm finish?' : 'Which finish?') + (open ? this.widget(this.materialWidget()) : '') + (answered ? ub(esc(this.finishOf(m.finish).name), this.ansFinish(m.finish)) : '');
      case 'mf-length': return this.bot('And how should it sit?') + (open ? this.widget(this.lengthWidget()) : '') + (answered ? ub(this.lengthLabel(m.length || this.defaultLength(o.lengths)), this.ansLength(m.length || this.defaultLength(o.lengths))) : '');
      case 'mf-stones': return this.bot(`Beautiful. Shall we give <b>this piece more meaning</b> too? Its own birthstones:`) + (open ? this.widget(this.stonesWidget()) : '') + (answered ? ub(this.stoneCount(m) ? 'Birthstones: ' + esc(this.stonesLabel(m, o)) : 'No birthstones for this one', this.ansStones(m, o.stoneFiles, o.stoneImages, o)) : '');
      case 'mf-gifts': return this.bot(`And shall I make this piece <b>gift-ready</b> as well?`) + (open ? this.widget(this.giftsWidget()) : '') + (answered ? ub(this.giftsReply(m), this.ansGifts(m)) : '');
      case 'mf-summary': return this.bot(`Here it is, your <b>${esc(o.title || 'piece')}</b>, ready to join the set:`) + this.bundleSummaryCard(o);
    }
    return '';
  }
  bundleSummaryCard(o) {
    const S = this.state, m = S.mf || {};
    const k = S.bEdit;
    const row = (key, label, value, editor) => `<div class="cht__sum-row" data-action="c-b-edit" data-k="${key}"><span class="cht__sum-label">${label}</span><span class="cht__sum-val">${value} <span class="cht__sum-pen">✎</span></span></div>${k === key ? `<div class="cht__sum-editor">${editor}</div>` : ''}`;
    const rows = [];
    if (o.hasCord) rows.push(row('cord', 'Cord', esc(this.cordOf(m.cord, o.cords).name), this.cordsFor().map((c) => `<button ${this.chipB(m.cord === c.id)} data-action="c-bce-cord" data-id="${c.id}"><span class="cht__chip-dot" style="background:${c.c}"></span>${c.name}</button>`).join('')));
    rows.push(row('finish', 'Finish', esc(this.finishOf(m.finish).name), this.finishList(o.finishIds).map((f) => `<button ${this.chipB(m.finish === f.id)} data-action="c-bce-finish" data-id="${f.id}"><span class="cht__chip-dot" style="background:${f.grad}"></span>${f.name}</button>`).join('')));
    if (o.hasLength) rows.push(row('length', 'Chain length', this.lengthLabel(m.length || this.defaultLength(o.lengths)), this.lengthsFor().map((L) => `<button ${this.chipB(m.length === L)} data-action="c-bce-length" data-n="${L}">${L} in · ${Math.round(L * 2.5)} cm</button>`).join('')));
    if (o.multiCharm) {
      rows.push(row('charms', 'Charms', esc(this.charmsLabel(m)), this.charmChips(m, 'c-bce-charms', null, o.maxCharms)));
      rows.push(row('name', 'Names', '“' + esc(this.namesDisplayOf(m.nameText)) + '”', `<div class="cht__charm-fields">${this.charmFieldsHtml(m, 'mf-cname')}</div><button class="cht__send" data-action="c-b-edit" data-k="name">Done</button>`));
    } else rows.push(row('name', 'Monogram', '“' + esc(this.namesDisplayOf(m.nameText)) + '”', `<div class="cht__input-row"><input class="cfg__input" data-input="bce-name" value="${esc(S.bceName || m.nameText || '')}" maxlength="40" autocomplete="off"><button class="cht__send" data-action="c-bce-name-save">Save</button></div>`));
    rows.push(row('style', 'Monogram style', esc((this.styleOf(m.style) || {}).name || '…'), STYLES.map((s) => `<button ${this.chipB(m.style === s.id)} data-action="c-bce-style" data-id="${s.id}">${this.styleThumbHtml(s, m.finish)}${s.name}</button>`).join('')));
    if (!o.hideStones) {
      const n = this.stoneCount(m);
      rows.push(row('stones', 'Birthstones', n ? esc(n + (n > 1 ? ' stones' : ' stone') + ' · +' + this.fmt(n * o.stonePrice)) : 'None', `<div class="cfg__stones-row" style="padding:2px;margin:0 -2px">${this.stoneCardsHtml(m, { mini: true, stonePrice: o.stonePrice, stoneFiles: o.stoneFiles, stoneImages: o.stoneImages, stones: o.stones || null })}</div>`));
    }
    const gN = (m.scratch ? 1 : 0) + (m.gift ? 1 : 0);
    rows.push(row('gifts', 'Gift extras', gN ? esc(gN + (gN > 1 ? ' extras' : ' extra')) : 'None', `<button ${this.chipB(!!m.scratch)} data-action="c-bce-scratch">${m.scratch ? '✓' : '+'} Scratch card · ${this.fmt(this.cfg.scratch.price)}</button><button ${this.chipB(!!m.gift)} data-action="c-bce-gift">${m.gift ? '✓' : '+'} Gift box · ${this.fmt(this.cfg.gift.price)}</button>`));
    const base = o.dealPrice != null ? o.dealPrice : o.price;
    const total = base + this.addonPrice(m, { stonePrice: o.stonePrice });
    return `<div class="cht__sum" data-c-bsummary>
      <div class="cht__sum-head"><span class="cht__sum-title cfg-serif">${esc(o.title)} “${esc(this.namesDisplayOf(m.nameText))}”</span><span class="cht__sum-price">${this.fmt(base + this.charmExtra(m))}</span></div>
      <span class="cht__sum-kicker">TAP ANY DETAIL TO CHANGE IT</span>
      <div class="cht__sum-rows">${rows.join('')}</div>
      <button class="cfg__btn-solid cht__continue" data-action="mf-add"${S.busy || !this.mfNameValid() ? ' disabled' : ''}>${S.busy ? 'Adding…' : !this.mfNameValid() ? 'Add a name for each charm' : 'Add to my set · ' + this.fmt(total)}</button>
      <button class="cht__link" data-action="c-b-cancel">Not now</button>
    </div>`;
  }
}

if (!customElements.get('configurator-chat-element')) {
  customElements.define('configurator-chat-element', ConfiguratorChat);
}
